import { createContext, useMemo } from 'react';

import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';

import useSubscription from '../_hooks/useSubscription';

export type ContextType = {
  publication: Publication;
  publicationIsPrivate: boolean;
  publicationRequiresSubscriberApproval: boolean;
  isLoggedIn: boolean;
  isInactive: boolean;
  isPremiumTier: boolean;
  canUnsubscribe: boolean;
  canDowngrade: boolean;
  email: string;
};

const AccountActionContext = createContext<ContextType | undefined>(undefined);
AccountActionContext.displayName = 'AccountActionContext';

const AccountActionProvider = ({
  publication,
  isLoggedIn,
  subscription,
  children,
}: {
  publication: Publication;
  isLoggedIn: boolean;
  subscription: Subscription;
  children: React.ReactNode;
}) => {
  const { email } = subscription;

  const { isInactive, isPremiumTier, canUnsubscribe, canDowngrade } = useSubscription(subscription, isLoggedIn);

  const { private: publicationIsPrivate, require_subscriber_approval: publicationRequiresSubscriberApproval } =
    publication;

  const context: ContextType = useMemo(
    () => ({
      publication,
      publicationIsPrivate,
      publicationRequiresSubscriberApproval,
      isLoggedIn,
      isInactive,
      isPremiumTier,
      canUnsubscribe,
      canDowngrade,
      email,
    }),
    [
      publication,
      publicationIsPrivate,
      publicationRequiresSubscriberApproval,
      isLoggedIn,
      isInactive,
      isPremiumTier,
      canUnsubscribe,
      canDowngrade,
      email,
    ],
  );

  return <AccountActionContext.Provider value={context}>{children}</AccountActionContext.Provider>;
};

export { AccountActionContext, AccountActionProvider };
